import React, { useState, useEffect } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useParams, Link as RouterLink, useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

// material-ui
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Skeleton from '@material-ui/lab/Skeleton';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Avatar from '@material-ui/core/Avatar';

import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import BackIcon from '@material-ui/icons/KeyboardArrowLeft';

// local
import DetailSkeleton from 'app/components/loaders/DetailSkeleton';
import EditProposalDialog from 'app/components/election/EditProposalDialog';
import EditRecommendationDialog from 'app/components/election/EditRecommendationDialog'
import RedButton from 'app/components/buttons/RedButton';

import useApi from 'app/context/useApi';

import formatValue from 'app/utils/FormatValue';

const useStyles = makeStyles(theme => ({
    container: {
        padding: theme.spacing(3),
    },
    outlinedSection: {
        border: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: theme.shape.borderRadius,
        marginBottom: theme.spacing(3),
        padding: theme.spacing(3)
    },
    section: {
        marginBottom: theme.spacing(3)
    },
    empty: {
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.shape.borderRadius,
        textAlign: 'center',
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    }
}));

export default function EditProposal(props) {

    const { election } = props;

    const classes = useStyles();
    const params = useParams();
    const history = useHistory();
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const api = useApi();

    const proposalID = params.id;

    const [ proposal, setProposal ] = useState(null);

    const [ editProposalDialogOpen, setEditProposalDialogOpen ] = useState(false);
    const [ deleteProposalDialogOpen, setDeleteProposalDialogOpen ] = useState(false);
    const [ editRecommendationDialogOpen, setEditRecommendationDialogOpen ] = useState(false);
    const [ editingRecommendation, setEditingRecommendation ] = useState(false);

    const [ loading, setLoading ] = useState(false);

    // Getters ------------------------------------------

    const getProposal = ($quiet = false) => {

        if (! $quiet) setLoading(true);

        api.get(`/admin/proposal/${proposalID}`)
            .then(res => setProposal(res.proposal))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        getProposal();
    }, []);

    const getValueComponent = value => {
        switch (value.field.options.type) {
            case 'upload':
                if (value.value.file) {
                    return (<Link href="#" onClick={() => downloadFile(value.field_id, value.value.file.id)}>{value.value.file.name}</Link>);
                }
                return (<Typography variant="body2" color="textSecondary">No File Selected</Typography>);
            default:
                return (<Typography variant="body2">{formatValue(value.value.value, value.field.options.type)}</Typography>);
        }
    }

    const downloadFile = (field_id, file_id) => {
        api.get(`/field/${field_id}/file/${file_id}`);
    }

    // Edit Proposal Dialog Functions ----------------------------

    const openEditProposalDialog = () => {
        setEditProposalDialogOpen(true);
    }

    const closeEditProposalDialog = () => {
        setEditProposalDialogOpen(false);
    }

    const closeEditProposalDialogAndRefresh = () => {
        getProposal();
        closeEditProposalDialog(false);
    }

    // Delete Proposal Dialog Functions -----------------------------------------------

    const openDeleteProposalDialog = () => {
        setDeleteProposalDialogOpen(true);
    }

    const closeDeleteProposalDialog = () => {
        setDeleteProposalDialogOpen(false);
    }

    const deleteProposal = () => {

        setLoading(true);

        api.delete(`/admin/proposal/${proposalID}`)
            .then(() => history.push(`/admin/elections/${election.id}/proposals`))
            .catch(() => {
                closeDeleteProposalDialog();
                setLoading(false);
            })
    }

    // Recommendation Dialog Functions -----------------------


    const openEditRecommendationDialog = (recommendation = false) => {
        setEditingRecommendation(recommendation);
        setEditRecommendationDialogOpen(true);
    }

    const closeEditRecommendationDialog = () => {
        setEditRecommendationDialogOpen(false);
    }

    const closeEditRecommendationDialogAndRefresh = () => {
        getProposal();
        closeEditRecommendationDialog(false);
    }

    const editRecommendationDialogClosed = () => {
        setEditingRecommendation(false);
    }

    const initials = user => {
        console.log(user);
        return user.first.charAt(0) + user.last.charAt(0);
    }

    return (
        <div className={classes.container}>
            <Grid container alignItems='center' justify="space-between" spacing={3} className={classes.section}>
                <Grid item>
                    {!proposal ? (
                        <Skeleton animation="wave" height={20} width="60%"  />
                    ) : (
                        <>
                            <Typography variant="h5" color="secondary" gutterBottom>
                                {proposal.title}
                            </Typography>
                            {proposal.subtitle && (proposal.subtitle != '') &&
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    {proposal.subtitle}
                                </Typography>
                            }
                            <Typography variant="caption" color="secondary" >
                                Proposal {proposal.id}
                            </Typography>
                        </>
                    )}
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        to={`/admin/elections/${election.id}/proposals`}
                        component={RouterLink}
                        startIcon={<BackIcon />}
                        >
                        Back to Proposals
                    </Button>
                </Grid>
            </Grid>
            <div className={classes.outlinedSection}>
                {(!proposal) ? (
                    <DetailSkeleton />
                ) : (
                    <>
                        {proposal.values.map((value, index) => (
                            <Grid key={index} container spacing={3}>
                                <Grid item xs={3}>
                                    <Typography variant="subtitle2" color="textSecondary">{value.field.options.label}</Typography>
                                </Grid>
                                <Grid item xs={9}>
                                    {getValueComponent(value)}
                                </Grid>
                            </Grid>
                        ))}
                        <Grid container spacing={3}>
                            <Grid item xs={3}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Sponsored By:
                                </Typography>
                            </Grid>
                            <Grid item xs={9}>
                                <Typography variant="body2">
                                    {proposal.sponsors}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid container alignItems='center' justify="flex-end" spacing={3}>
                            <Grid item>
                                <RedButton
                                    variant="contained"
                                    onClick={openDeleteProposalDialog}
                                    size="small"
                                    >
                                    Delete Proposal
                                </RedButton>
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="contained"
                                    onClick={openEditProposalDialog}
                                    size="small"
                                    >
                                    Edit Proposal
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                )}
            </div>
            <Grid container alignItems='center' justify="space-between" spacing={3} className={classes.section}>
                <Grid item>
                    <Typography variant="h6" color="textSecondary" align="center" gutterBottom >
                        Recommendations
                    </Typography>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => openEditRecommendationDialog()}
                        >
                        New Recommendation
                    </Button>
                </Grid>
            </Grid>
            {proposal && (proposal.recommendations.length > 0) ? proposal.recommendations.map(r =>
                <Card raised className={classes.marginBottom} key={r.id}>
                    <CardHeader
                        avatar={
                            <Avatar
                                aria-label={r.user.full_name}
                                alt={r.user.full_name}
                                src={r.user.avatar ? r.user.avatar.full_url : null}
                                >
                                {initials(r.user)}
                            </Avatar>
                        }
                        action={
                            <Tooltip title="Edit Recommendation">
                                <IconButton onClick={() => openEditRecommendationDialog(r)}>
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        }
                        title={`Recommendation by ${r.user.full_name}`}
                        subheader={DateTime.fromISO(r.created_at.toString()).toLocaleString(DateTime.DATE_MED)}
                    />
                    <CardContent>
                        <Typography variant="body2" color="textSecondary">
                            {r.content}
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Typography variant="subtitle2" className={classes.empty} color="textSecondary">
                    This proposal does not have any recommendations. <Link href="#" onClick={() => openEditRecommendationDialog()}>Click Here</Link> to create the first recommendation for this proposal.
                </Typography>
            )}
            {proposal &&
                <>
                    <EditProposalDialog
                        election={election}
                        proposal={proposal}
                        open={editProposalDialogOpen}
                        onClose={closeEditProposalDialog}
                        onSave={closeEditProposalDialogAndRefresh}
                        onDelete={closeEditProposalDialogAndRefresh}
                        admin
                    />
                    <EditRecommendationDialog
                        election={election}
                        proposal={proposal}
                        recommendation={editingRecommendation}
                        open={editRecommendationDialogOpen}
                        onClose={closeEditRecommendationDialog}
                        onSave={closeEditRecommendationDialogAndRefresh}
                        onDelete={closeEditRecommendationDialogAndRefresh}
                        onClosed={editRecommendationDialogClosed}
                        admin
                    />
                </>
            }

            <Dialog
                open={deleteProposalDialogOpen}
                onClose={closeDeleteProposalDialog}
                fullWidth
                maxWidth="sm"
                aria-labelledby="delete-proposal"
                >
                <DialogTitle id="delete-proposal">
                    Delete Proposal
                </DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this Proposal?
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={closeDeleteProposalDialog}>
                        Cancel
                    </Button>
                    <RedButton variant="contained" onClick={deleteProposal}>
                        Delete Proposal
                    </RedButton>
                </DialogActions>
            </Dialog>
        </div>
    )
}
